import React, { useState } from "react";
import { motion } from "framer-motion";

import qrCode from "./Images/qr.png";

import {
  Calendar,
  ClipboardCheck,
  Mail,
  Copy,
  Check,
  Info,
  Clock,
  IndianRupee,
  ArrowRight,
} from "lucide-react";

// ── Helpers ───────────────────────────────────────────────────────
const TODAY = new Date("2026-05-24");

const DATES = [
  {
    id: 1,
    label: "Last date for submission of abstract",
    date: "June 15, 2026",
    iso: "2026-06-15",
  },
  {
    id: 2,
    label: "Last date for acceptance of abstract",
    date: "June 30, 2026",
    iso: "2026-06-30",
  },
  {
    id: 3,
    label: "Last date for submission of full-length paper",
    date: "July 15, 2026",
    iso: "2026-07-15",
  },
  {
    id: 4,
    label: "Last date for acceptance of full-length paper",
    date: "July 20, 2026",
    iso: "2026-07-20",
  },
  {
    id: 5,
    label: "Last date of registration",
    date: "July 25, 2026",
    iso: "2026-07-25",
  },
];

const FEES = [
  {
    category: "Students & PhD Scholars",
    amount: "₹ 4,500/-",
    gst: "Incl. 18% GST",
  },
  {
    category: "Academicians",
    amount: "₹ 5,000/-",
    gst: "Incl. 18% GST",
  },
  {
    category: "Industry Professionals",
    amount: "₹ 6,000/-",
    gst: "Incl. 18% GST",
  },
];

const EMAIL = "ruceconference@reva.edu.in";

function getStatus(iso) {
  const diff = Math.ceil((new Date(iso) - TODAY) / 86400000);

  if (diff < 0)
    return {
      label: "Closed",
      text: "text-gray-400",
      bg: "bg-gray-100",
      dot: "bg-gray-300",
    };

  if (diff === 0)
    return {
      label: "Today",
      text: "text-orange-600",
      bg: "bg-orange-100",
      dot: "bg-orange-500",
    };

  if (diff <= 21)
    return {
      label: `${diff}d left`,
      text: "text-orange-600",
      bg: "bg-orange-50",
      dot: "bg-orange-400",
    };

  return {
    label: `${diff}d left`,
    text: "text-emerald-600",
    bg: "bg-emerald-50",
    dot: "bg-emerald-500",
  };
}

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.1 },
  transition: { duration: 0.5, ease: "easeOut", delay },
});

// ─────────────────────────────────────────────────────────────────
export default function DatesAndRegistration() {
  const [copied, setCopied] = useState(false);

  /* copy email */
  const handleCopy = () => {
    navigator.clipboard.writeText(EMAIL).then(() => {
      setCopied(true);

      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <section className="w-full bg-gray-50 border-y border-gray-100">
      <div className="max-w-screen-xl mx-auto px-6 lg:px-12 py-20 lg:py-28">
        {/* ── Section Header ── */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <div>
            <motion.div
              {...fadeUp(0.05)}
              className="flex items-center gap-3 mb-5"
            >
              <div className="h-px w-8 bg-[#F58220]" />

              <span className="text-[#F58220] text-[11px] font-bold tracking-[0.22em] uppercase">
                SMPACE-2026
              </span>
            </motion.div>

            <motion.h2
              {...fadeUp(0.1)}
              className="text-gray-900 text-3xl lg:text-[42px] font-black leading-tight tracking-tight"
            >
              Dates &amp;
              <span className="text-[#F58220]"> Registration</span>
            </motion.h2>

            <motion.p
              {...fadeUp(0.16)}
              className="text-gray-500 text-[15px] mt-3 max-w-xl leading-relaxed"
            >
              All key deadlines and registration fee details for SMPACE-2026 —
              conducted in{" "}
              <span className="text-gray-800 font-semibold">
                offline mode only
              </span>{" "}
              at REVA University, Bengaluru.
            </motion.p>
          </div>

          {/* Offline badge */}
          <motion.div {...fadeUp(0.2)} className="flex-shrink-0">
            <div className="inline-flex items-center gap-3 bg-[#111827] rounded-2xl px-6 py-4">
              <div className="w-9 h-9 rounded-xl bg-[#F58220]/15 flex items-center justify-center">
                <Clock size={17} className="text-[#F58220]" />
              </div>

              <div>
                <p className="text-white text-[14px] font-black">
                  Offline Mode Only
                </p>

                <p className="text-white/50 text-[12px]">
                  July 31 – Aug 01, 2026 · Bengaluru
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* ── Two Column Layout ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* ══ LEFT — IMPORTANT DATES ══ */}
          <motion.div
            {...fadeUp(0.1)}
            className="bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm"
          >
            {/* Card header */}
            <div className="flex items-center gap-3 px-7 py-5 border-b border-gray-100">
              <div className="w-9 h-9 rounded-xl bg-orange-50 flex items-center justify-center">
                <Calendar size={17} className="text-[#F58220]" />
              </div>

              <div>
                <p className="text-gray-900 text-[15px] font-black">
                  Important Dates
                </p>

                <p className="text-gray-400 text-[12px]">
                  All submission &amp; review deadlines
                </p>
              </div>
            </div>

            {/* Timeline */}
            <div className="px-7 py-6">
              {DATES.map((d, i) => {
                const s = getStatus(d.iso);

                const isLast = i === DATES.length - 1;

                return (
                  <motion.div
                    key={d.id}
                    initial={{ opacity: 0, x: -16 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{
                      delay: 0.1 + i * 0.08,
                      duration: 0.45,
                    }}
                    className="flex gap-4"
                  >
                    {/* Spine */}
                    <div className="flex flex-col items-center">
                      <div className="w-9 h-9 rounded-full border-2 border-[#F58220] bg-orange-50 flex items-center justify-center flex-shrink-0 text-[12px] font-black text-[#F58220]">
                        {String(i + 1).padStart(2, "0")}
                      </div>

                      {!isLast && (
                        <div className="w-px flex-1 my-1.5 bg-orange-100 min-h-[28px]" />
                      )}
                    </div>

                    {/* Content */}
                    <div className={`flex-1 ${isLast ? "pb-0" : "pb-5"}`}>
                      <p className="text-gray-500 text-[12px] leading-snug mb-1">
                        {d.label}
                      </p>

                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-gray-900 text-[14px] font-black">
                          {d.date}
                        </span>

                        <span
                          className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-0.5 rounded-full ${s.bg} ${s.text}`}
                        >
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${s.dot}`}
                          />

                          {s.label}
                        </span>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Conference date banner */}
            <div className="mx-7 mb-7 bg-[#111827] rounded-xl px-5 py-4 flex items-center justify-between gap-3">
              <div>
                <p className="text-white/50 text-[10px] font-bold tracking-widest uppercase">
                  Conference Dates
                </p>

                <p className="text-white text-[15px] font-black mt-0.5">
                  July 31 &amp; August 01, 2026
                </p>
              </div>

              <span className="bg-[#F58220] text-white text-[10px] font-black tracking-wider uppercase px-3 py-1.5 rounded-lg">
                SMPACE-2026
              </span>
            </div>
          </motion.div>

          {/* ══ RIGHT — FEES + SUBMISSION ══ */}
          <div className="flex flex-col gap-6">
            {/* Fee Table Card */}
            <motion.div
              {...fadeUp(0.15)}
              className="bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm"
            >
              <div className="flex items-center gap-3 px-7 py-5 border-b border-gray-100">
                <div className="w-9 h-9 rounded-xl bg-orange-50 flex items-center justify-center">
                  <IndianRupee size={17} className="text-[#F58220]" />
                </div>

                <div>
                  <p className="text-gray-900 text-[15px] font-black">
                    Registration Fee
                  </p>

                  <p className="text-gray-400 text-[12px]">
                    Indian participants — all fees include 18% GST
                  </p>
                </div>
              </div>

              <div className="px-7 py-5">
                {/* Table header */}
                <div className="grid grid-cols-2 pb-3 mb-1 border-b border-gray-100">
                  <span className="text-[10px] font-bold text-[#F58220] tracking-widest uppercase">
                    Category
                  </span>

                  <span className="text-[10px] font-bold text-[#F58220] tracking-widest uppercase text-right">
                    Amount
                  </span>
                </div>

                {/* Rows */}
                <div className="divide-y divide-gray-50">
                  {FEES.map((f, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: 12 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{
                        delay: 0.2 + i * 0.07,
                        duration: 0.4,
                      }}
                      className="grid grid-cols-2 items-center py-3.5 px-3 rounded-xl hover:bg-orange-50/60 transition-colors duration-150 group"
                    >
                      <span className="text-gray-700 text-[14px] font-medium group-hover:text-gray-900 transition-colors">
                        {f.category}
                      </span>

                      <div className="text-right">
                        <p className="text-gray-900 text-[16px] font-black">
                          {f.amount}
                        </p>

                        <p className="text-gray-400 text-[11px]">{f.gst}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Offline note */}
                <div className="mt-4 flex items-start gap-3 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3">
                  <Info
                    size={15}
                    className="text-blue-400 flex-shrink-0 mt-0.5"
                  />

                  <p className="text-blue-700 text-[12px] leading-relaxed">
                    This conference will be conducted in{" "}
                    <strong>offline mode only</strong>. At least one author of
                    the accepted paper must register and physically present the
                    paper.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Abstract Submission Card */}
            <motion.div
              {...fadeUp(0.2)}
              className="bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm flex-1"
            >
              <div className="flex items-center gap-3 px-7 py-5 border-b border-gray-100">
                <div className="w-9 h-9 rounded-xl bg-orange-50 flex items-center justify-center">
                  <ClipboardCheck
                    size={17}
                    className="text-[#F58220]"
                  />
                </div>

                <div>
                  <p className="text-gray-900 text-[15px] font-black">
                    Abstract Submission
                  </p>

                  <p className="text-gray-400 text-[12px]">
                    Scan QR or submit via email
                  </p>
                </div>
              </div>

              <div className="px-7 py-6 flex flex-col sm:flex-row gap-7">
                {/* QR Section */}
                <div className="flex-shrink-0">
                  <p className="text-gray-400 text-[10px] font-bold tracking-widest uppercase mb-3">
                    QR Code
                  </p>

                  <div className="relative">
                    <img
                      src={qrCode}
                      alt="QR Code"
                      className="w-[130px] h-[130px] object-contain rounded-xl border-2 border-[#F58220] shadow-sm bg-white p-2"
                    />
                  </div>
                </div>

                {/* Email Section */}
                <div className="flex-1 min-w-0 flex flex-col justify-between gap-5">
                  <div>
                    <p className="text-gray-400 text-[10px] font-bold tracking-widest uppercase mb-2">
                      Submission Email
                    </p>

                    <p className="text-[#F58220] text-[14px] font-bold break-all leading-snug">
                      {EMAIL}
                    </p>
                  </div>

                  <div className="flex flex-col gap-2.5">
                    {/* Copy email */}
                    <button
                      onClick={handleCopy}
                      className={`
                        flex items-center gap-2.5 px-4 py-2.5 rounded-lg border text-[13px] font-semibold
                        transition-all duration-200 w-full justify-center
                        ${
                          copied
                            ? "bg-emerald-50 border-emerald-200 text-emerald-600"
                            : "bg-orange-50 border-orange-200 text-[#F58220] hover:bg-orange-100"
                        }
                      `}
                    >
                      {copied ? (
                        <>
                          <Check size={14} /> Copied!
                        </>
                      ) : (
                        <>
                          <Copy size={14} /> Copy Email
                        </>
                      )}
                    </button>

                    {/* Send email */}
                    <a
                      href={`mailto:${EMAIL}`}
                      className="flex items-center gap-2.5 px-4 py-2.5 rounded-lg bg-[#F58220] hover:bg-[#d96d12] text-white text-[13px] font-bold transition-all duration-200 w-full justify-center group"
                    >
                      <Mail size={14} />
                      Send Email
                      <ArrowRight
                        size={13}
                        className="group-hover:translate-x-0.5 transition-transform"
                      />
                    </a>
                  </div>

                  {/* Deadline chip */}
                  <div className="flex items-center gap-2 bg-orange-50 border border-orange-100 rounded-xl px-4 py-2.5">
                    <Clock
                      size={13}
                      className="text-[#F58220] flex-shrink-0"
                    />

                    <p className="text-gray-600 text-[12px]">
                      Abstract deadline:&nbsp;
                      <span className="text-gray-900 font-bold">
                        June 15, 2026
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* ── Bottom CTA Banner ── */}
        <motion.div
          {...fadeUp(0.25)}
          className="mt-8 relative overflow-hidden bg-[#111827] rounded-2xl px-8 py-9"
        >
          <div className="absolute -top-12 -right-12 w-64 h-64 rounded-full bg-[#F58220]/8 blur-3xl pointer-events-none" />

          <div className="relative z-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="h-px w-6 bg-[#F58220]" />

                <span className="text-[#F58220] text-[10px] font-bold tracking-[0.22em] uppercase">
                  Paper Submission
                </span>
              </div>

              <h3 className="text-white text-[22px] font-black leading-snug">
                Ready to Submit Your Research?
              </h3>

              <p className="text-white/50 text-[13px] mt-1.5">
                Full papers: max 10 pages · min 4 pages · 300 dpi figures ·
                double-blind review
              </p>
            </div>

            <div className="flex flex-col sm:items-end gap-3 flex-shrink-0">
              <a
                href="/paper-submission"
                className="inline-flex items-center gap-2 bg-[#F58220] hover:bg-[#d96d12] text-white text-[13px] font-bold px-6 py-3 rounded-lg transition-all duration-200 group whitespace-nowrap"
              >
                View Submission Guidelines

                <ArrowRight
                  size={14}
                  className="group-hover:translate-x-1 transition-transform"
                />
              </a>

              <p className="text-white/35 text-[11px]">📧 {EMAIL}</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}